import React, { useState, useEffect, useContext } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { ThemeContext } from '../context/ThemeContext';
import Layout from '../components/layout/Layout';
import { API_BASE } from '../api';

function EditorTasks() {
  const [allTasks, setAllTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const { currentTheme } = useContext(ThemeContext);

  const { editorId, editorName, tasks: initialTasks } = location.state || {};

  const colors = {
    backgroundDark: '#0f0c29', 
    backgroundMedium: '#302b63', 
    backgroundLight: '#24243e',
    textLight: 'rgba(255, 255, 255, 0.8)',
    borderDark: 'rgba(255, 255, 255, 0.2)',
    primary: currentTheme?.primary || '#6366F1',
    secondary: currentTheme?.secondary || '#8B5CF6',
    accent: currentTheme?.accent || '#06B6D4',
    success: '#10B981',
    warning: '#F59E0B',
    danger: '#EF4444'
  };

  const styles = {
    container: {
      minHeight: '100vh',
      background: `linear-gradient(135deg, ${colors.backgroundDark} 0%, ${colors.backgroundMedium} 50%, ${colors.backgroundLight} 100%)`,
      position: 'relative',
      overflowX: 'hidden'
    },
    animatedBackground: { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' },
    floatingOrb: { position: 'absolute', borderRadius: '50%', filter: 'blur(40px)', opacity: 0.3, animation: 'float 8s ease-in-out infinite' },
    orb1: { width: '300px', height: '300px', background: colors.primary, top: '10%', left: '5%' },
    orb2: { width: '200px', height: '200px', background: colors.secondary, top: '60%', right: '10%' },
    orb3: { width: '250px', height: '250px', background: colors.accent, bottom: '20%', left: '15%' },
    orb4: { width: '180px', height: '180px', background: colors.success, top: '30%', right: '20%' },
    gridOverlay: {
      position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
      backgroundImage: `linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)`,
      backgroundSize: '50px 50px'
    },
    header: { padding: '30px 30px 20px', position: 'relative', zIndex: 2 },
    headerContent: { maxWidth: '1200px', margin: '0 auto' },
    greeting: { display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '10px' },
    pageTitle: { fontSize: '2.2rem', fontWeight: '800', background: 'linear-gradient(135deg, #fff 0%, #a5b4fc 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', margin: 0 },
    welcomeBadge: { background: 'rgba(255, 255, 255, 0.1)', padding: '6px 12px', borderRadius: '16px', border: `1px solid ${colors.borderDark}`, backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', gap: '6px' },
    badgeText: { color: 'white', fontWeight: '600', fontSize: '0.8rem' },
    badgeRole: { background: colors.primary, color: 'white', padding: '2px 6px', borderRadius: '10px', fontSize: '0.65rem', fontWeight: '600' },
    pageSubtitle: { color: colors.textLight, fontSize: '1rem', margin: 0 },
    content: { padding: '0 30px 30px', maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 2 },
    formCard: { background: 'rgba(255, 255, 255, 0.05)', borderRadius: '20px', border: `1px solid ${colors.borderDark}`, backdropFilter: 'blur(10px)', overflow: 'hidden', transition: 'all 0.3s ease', boxShadow: '0 8px 25px rgba(0, 0, 0, 0.2)' },
    cardHeader: { 
      background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`, 
      color: 'white', 
      padding: '18px 25px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: '10px'
    },
    cardTitle: { fontSize: '1.5rem', fontWeight: '700', margin: 0 },
    backBtn: { 
      background: 'rgba(255, 255, 255, 0.1)', 
      color: 'white', 
      border: `1px solid ${colors.borderDark}`, 
      padding: '10px 20px', 
      borderRadius: '10px', 
      fontWeight: '600', 
      fontSize: '0.9rem', 
      cursor: 'pointer', 
      display: 'flex', 
      alignItems: 'center', 
      gap: '6px', 
      textDecoration: 'none', 
      marginBottom: '15px',
      transition: 'all 0.3s ease'
    },
    spinner: { width: '16px', height: '16px', border: '2px solid transparent', borderTop: '2px solid white', borderRadius: '50%', animation: 'spin 1s linear infinite' },
    tableHeader: { 
      background: 'rgba(255, 255, 255, 0.1)', 
      color: 'white', 
      borderBottom: `2px solid ${colors.borderDark}`, 
      padding: '10px 8px', 
      fontWeight: '600',
      fontSize: '0.85rem',
      whiteSpace: 'nowrap'
    },
    tableRow: { borderBottom: '1px solid rgba(255, 255, 255, 0.05)', color: 'white' },
    tableCell: { 
      padding: '10px 8px', 
      verticalAlign: 'middle',
      fontSize: '0.85rem',
      whiteSpace: 'nowrap'
    },
    actionBtn: { 
      background: 'rgba(255, 255, 255, 0.1)', 
      color: 'white', 
      border: `1px solid ${colors.borderDark}`, 
      padding: '8px 16px', 
      borderRadius: '10px', 
      fontWeight: '600', 
      fontSize: '0.85rem', 
      cursor: 'pointer', 
      display: 'flex', 
      alignItems: 'center', 
      gap: '6px',
      transition: 'all 0.3s ease'
    },
    primaryBtn: { 
      background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`, 
      color: 'white', 
      border: 'none', 
      padding: '10px 20px', 
      borderRadius: '10px', 
      fontWeight: '600', 
      fontSize: '0.9rem', 
      cursor: 'pointer', 
      display: 'flex', 
      alignItems: 'center', 
      gap: '6px' 
    },
    downloadBtn: {
      background: `linear-gradient(135deg, ${colors.success}, #059669)`,
      color: 'white',
      border: 'none',
      padding: '8px 16px',
      borderRadius: '10px',
      fontWeight: '600',
      fontSize: '0.85rem',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      transition: 'all 0.3s ease'
    },
    tableNameBadge: {
      background: 'rgba(255, 255, 255, 0.1)',
      color: 'white',
      padding: '4px 10px',
      borderRadius: '12px',
      fontSize: '0.75rem',
      fontWeight: '500',
      border: `1px solid ${colors.borderDark}`,
      backdropFilter: 'blur(10px)'
    },
    filterBadge: {
      background: colors.accent,
      color: 'white',
      padding: '4px 10px',
      borderRadius: '12px',
      fontSize: '0.75rem',
      fontWeight: '500',
      marginLeft: '10px'
    }
  };

  useEffect(() => {
    if (initialTasks) {
      setAllTasks(initialTasks);
    } else if (editorId) {
      fetchAllEditorTasks();
    }
  }, [editorId, initialTasks]);

  const fetchAllEditorTasks = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/tasks_api.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          operation: 'get_all_editor_tasks', 
          editor_id: parseInt(editorId) 
        })
      });
      const data = await response.json();
      setAllTasks(data);
    } catch (error) {
      setError('Error loading editor tasks: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const isValidLink = (link) => {
    if (!link || typeof link !== 'string') return false;
    return !['-', 'Open', 'NULL', 'null', '', 'View', 'N/A'].includes(link.trim());
  };

  const getEditedStatus = (task) => {
    let status = task.edited_status;
    if (!status || status === 'pending') {
      const hasEditedLink = isValidLink(task.edited_video_link) || isValidLink(task.edited_link);
      status = hasEditedLink ? 'completed' : 'pending';
    }
    const statusConfig = {
      completed: { class: 'bg-success text-white', text: 'Edited', icon: 'fa-check-circle' },
      pending: { class: 'bg-secondary text-white', text: 'Not Edited', icon: 'fa-pen' }
    };
    return statusConfig[status] || statusConfig.pending;
  };

  const getEditedVideoLink = (task) => task.edited_video_link || task.edited_link || '';

  const getStatusBadge = (status) => {
    const statusConfig = {
      completed: { class: 'bg-success text-white', text: 'Completed', icon: 'fa-check' },
      approved: { class: 'bg-success text-white', text: 'Approved', icon: 'fa-check-circle' },
      assigned: { class: 'bg-primary text-white', text: 'Assigned', icon: 'fa-user-check' },
      pending: { class: 'bg-warning text-dark', text: 'Pending', icon: 'fa-clock' }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return <span className={`badge ${config.class} fs-6`}><i className={`fas ${config.icon} me-1`}></i>{config.text}</span>;
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === 'NULL') return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const truncateText = (text, maxLength = 30) => {
    if (!text || text === '-' || text === 'NULL') return '-';
    return text.length <= maxLength ? text : text.substring(0, maxLength) + '...';
  };

  const getTableNameColor = (tableName) => {
    switch(tableName) {
      case 'tasks': return colors.primary;
      case 'editor_tasks': return colors.secondary;
      default: return colors.accent;
    }
  };

  const downloadCSV = () => {
    if (!allTasks.length) return;

    const headers = [
      'Table',
      'ID',
      'Video Name',
      'Client',
      'Raw Link',
      'Count',
      'Shoot Date',
      'Operator',
      'Status',
      'Edited Status',
      'Edited Link'
    ];

    const csvRows = allTasks.map(task => {
      const editedStatus = getEditedStatus(task).text;
      const editedVideoLink = getEditedVideoLink(task);
      const row = [
        task.table_name || 'tasks',
        task.id ?? '',
        task.video_name ?? 'N/A',
        task.client_name ?? 'Unknown Client',
        task.raw_link ?? 'N/A',
        task.video_count ?? 0,
        task.shoot_date ?? 'N/A',
        task.operator_name ?? 'N/A',
        task.status ?? 'pending',
        editedStatus,
        editedVideoLink || 'N/A'
      ];

      return row.map(cell => {
        const value = cell === null || cell === undefined ? '' : String(cell);
        if (value.includes(',') || value.includes('"') || value.includes('\n')) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      }).join(',');
    });

    const csvContent = [headers.join(','), ...csvRows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${(editorName || 'editor').replace(/\s+/g, '_')}_all_tasks_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Count tasks by table
  const tasksByTable = allTasks.reduce((acc, task) => {
    const tableName = task.table_name || 'tasks';
    acc[tableName] = (acc[tableName] || 0) + 1;
    return acc;
  }, {});

  if (loading) return <Layout><div style={styles.container}><div style={styles.loadingState}>Loading...</div></div></Layout>;

  return (
    <Layout>
      <div style={styles.container}>
        <div className="animated-background" style={styles.animatedBackground}>
          <div className="floating-orb orb-1" style={{...styles.floatingOrb, ...styles.orb1}}></div>
          <div className="floating-orb orb-2" style={{...styles.floatingOrb, ...styles.orb2}}></div>
          <div className="floating-orb orb-3" style={{...styles.floatingOrb, ...styles.orb3}}></div>
          <div className="floating-orb orb-4" style={{...styles.floatingOrb, ...styles.orb4}}></div>
          <div className="grid-overlay" style={styles.gridOverlay}></div>
        </div>

        <div style={styles.header}>
          <div style={styles.headerContent}>
            <div style={styles.greeting}>
              <h1 style={styles.pageTitle}>Editor Tasks</h1>
              <div style={styles.welcomeBadge}>
                <span style={styles.badgeText}>Tables</span>
                <span style={styles.badgeRole}>tasks + editor_tasks</span>
              </div>
            </div>
            <p style={styles.pageSubtitle}>All tasks from multiple tables assigned to {editorName || 'Editor'}</p>
          </div>
        </div>

        <div style={styles.content}>
          <div style={{display: 'flex', gap: '12px', marginBottom: '15px', flexWrap: 'wrap'}}>
            <button style={styles.backBtn} onClick={() => navigate(-1)}><i className="fas fa-arrow-left me-2"></i>Back</button>
            <button style={styles.actionBtn} onClick={fetchAllEditorTasks}><i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''} me-2`}></i>Refresh</button>
            <button style={styles.downloadBtn} onClick={downloadCSV} disabled={!allTasks.length}>
              <i className="fas fa-file-csv me-2"></i>Download CSV
            </button>
          </div>

          <div className="tasks-card" style={styles.formCard}>
            <div style={styles.cardHeader}>
              <div>
                <h3 style={styles.cardTitle}>{editorName} - All Tasks List</h3>
                <div style={{display: 'flex', gap: '10px', marginTop: '5px', flexWrap: 'wrap'}}>
                  <span style={{fontSize: '0.85rem', opacity: 0.9}}>{allTasks.length} total tasks</span>
                  {Object.entries(tasksByTable).map(([tableName, count]) => (
                    <span key={tableName} style={styles.filterBadge}>
                      {tableName}: {count}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div style={{overflowX: 'auto'}}>
              <table className="table table-hover mb-0" style={{width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem'}}>
                <thead>
                  <tr>
                    <th style={styles.tableHeader}>Table</th>
                    <th style={styles.tableHeader}>ID</th>
                    <th style={styles.tableHeader}>Video Name</th>
                    <th style={styles.tableHeader}>Client</th>
                    <th style={styles.tableHeader}>Raw Link</th>
                    <th style={styles.tableHeader}>Count</th>
                    <th style={styles.tableHeader}>Shoot Date</th>
                    <th style={styles.tableHeader}>Operator</th>
                    <th style={styles.tableHeader}>Status</th>
                    <th style={styles.tableHeader}>Edited Status</th>
                    <th style={styles.tableHeader}>Edited Link</th>
                  </tr>
                </thead>
                <tbody>
                  {allTasks.map((task) => {
                    const editedStatus = getEditedStatus(task);
                    const editedVideoLink = getEditedVideoLink(task);
                    const tableName = task.table_name || 'tasks';
                    
                    return (
                      <tr key={`${tableName}-${task.id}`} style={styles.tableRow}>
                        {/* Table Name Column */}
                        <td style={{
                          ...styles.tableCell, 
                          fontWeight: '600',
                          color: getTableNameColor(tableName)
                        }}>
                          {tableName}
                        </td>
                        
                        {/* ID */}
                        <td style={{...styles.tableCell, fontWeight: 'bold', color: '#FFFFFF'}}>
                          #{task.id}
                          {tableName === 'editor_tasks' && (
                            <small style={{color: colors.accent, marginLeft: '5px', fontSize: '0.7rem'}}></small>
                          )}
                        </td>
                        
                        {/* Video Name */}
                        <td style={styles.tableCell}>
                          <button 
                            className="btn btn-link p-0 text-decoration-none border-0 bg-transparent"
                            onClick={() => navigate('/task-details', { state: { task } })}
                            style={{ color: '#FFFFFF', fontWeight: '600', textAlign: 'left', fontSize: '0.85rem' }}
                          >
                            {truncateText(task.video_name || 'N/A', 20)}
                          </button>
                        </td>
                        
                        {/* Client */}
                        <td style={styles.tableCell}>
                          <button 
                            className="btn btn-link p-0 text-decoration-none border-0 bg-transparent"
                            onClick={() => navigate('/client-tasks', { state: { clientId: task.client_id, clientName: task.client_name } })}
                            style={{ color: '#FFFFFF', fontWeight: '600', textAlign: 'left', fontSize: '0.85rem' }}
                          >
                            {truncateText(task.client_name || 'Unknown Client', 15)}
                          </button>
                        </td>
                        
                        {/* Raw Link */}
                        <td style={styles.tableCell}>
                          <button style={{
                            background: isValidLink(task.raw_link) ? colors.primary : 'rgba(255,255,255,0.1)', 
                            color: 'white', 
                            border: '1px solid rgba(255,255,255,0.2)', 
                            padding: '4px 10px', 
                            borderRadius: '6px',
                            fontSize: '0.8rem'
                          }} onClick={() => isValidLink(task.raw_link) && window.open(task.raw_link, '_blank')}>
                            {isValidLink(task.raw_link) ? 'Open' : '-'}
                          </button>
                        </td>
                        
                        {/* Count */}
                        <td style={styles.tableCell}>
                          <span className="badge" style={{
                            background: colors.accent, 
                            color: 'white',
                            fontSize: '0.8rem',
                            padding: '4px 8px'
                          }}>{task.video_count || '0'}</span>
                        </td>
                        
                        {/* Shoot Date */}
                        <td style={styles.tableCell}>
                          <small style={{ color: colors.textLight, fontSize: '0.8rem' }}>{formatDate(task.shoot_date)}</small>
                        </td>
                        
                        {/* Operator */}
                        <td style={styles.tableCell}>
                          <button 
                            className="btn btn-link p-0 text-decoration-none border-0 bg-transparent"
                            onClick={() => navigate('/operator-tasks', { state: { operatorId: task.operator_id, operatorName: task.operator_name } })}
                            style={{ 
                              color: '#FFFFFF', 
                              fontWeight: '600', 
                              textAlign: 'left', 
                              fontSize: '0.85rem' 
                            }}
                            disabled={!task.operator_id}
                          >
                            {truncateText(task.operator_name || 'N/A', 15)}
                          </button>
                        </td>
                        
                        {/* Status */}
                        <td style={styles.tableCell}>
                          <span style={{fontSize: '0.8rem'}}>
                            {getStatusBadge(task.status)}
                          </span>
                        </td>
                        
                        {/* Edited Status */}
                        <td style={styles.tableCell}>
                          <span className={`badge ${editedStatus.class}`} style={{fontSize: '0.8rem', padding: '4px 8px'}}>
                            {editedStatus.text}
                          </span>
                        </td>
                        
                        {/* Edited Link */}
                        <td style={styles.tableCell}>
                          <button style={{
                            background: isValidLink(editedVideoLink) ? colors.success : 'rgba(255,255,255,0.1)', 
                            color: 'white', 
                            border: '1px solid rgba(255,255,255,0.2)', 
                            padding: '4px 10px', 
                            borderRadius: '6px',
                            fontSize: '0.8rem'
                          }} onClick={() => isValidLink(editedVideoLink) && window.open(editedVideoLink, '_blank')}>
                            {isValidLink(editedVideoLink) ? 'View' : '-'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <style jsx>{`
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        @keyframes float { 0%, 100% { transform: translate(0px, 0px) scale(1); } 33% { transform: translate(30px, -50px) scale(1.1); } 66% { transform: translate(-20px, 20px) scale(0.9); } }
        
        .badge {
          font-size: 0.8rem;
          padding: 4px 8px;
        }
      `}</style>
    </Layout>
  );
}

export default EditorTasks;